import { memo, useMemo, useState, useCallback, useEffect } from 'react';

import { Row, Col, Tag, Tooltip, message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { logEvent } from '@refly/telemetry-web';
// styles
import './index.scss';
import { Spin } from '@refly-packages/ai-workspace-common/components/common/spin';
import { Wait } from 'refly-icons';
import plusIcon from '@refly-packages/ai-workspace-common/assets/plus-icon.svg';
import getClient, { client } from '@refly-packages/ai-workspace-common/requests/proxiedRequest';
import {
  useSubscriptionStoreShallow,
  useUserStoreShallow,
  useAuthStoreShallow,
} from '@refly/stores';
import { useNavigate } from '@refly-packages/ai-workspace-common/utils/router';
import { SubscriptionPlanType, SubscriptionPlan, Voucher } from '@refly/openapi-schema';
import { SUBSCRIPTION_PRICES } from '@refly-packages/ai-workspace-common/constants/pricing';

export type SubscriptionInterval = 'monthly' | 'yearly';
export type PriceSource = 'page' | 'modal';

const gridSpan = {
  xs: 24,
  sm: 24,
  md: 8,
  lg: 8,
  xl: 8,
  xxl: 8,
};

interface Feature {
  name: string;
  type?: string;
  items?: string[];
  duration?: string;
}

enum PlanPriorityMap {
  free = 0,
  plus = 1,
  plus_pro = 2,
  starter = 2,
  maker = 2,
  enterprise = 3,
}

// Voucher discount tag - orange style
const VoucherTag = memo(
  ({ discountPercent, validDays }: { discountPercent: number; validDays: number }) => {
    const { t, i18n } = useTranslation('ui');
    const isZh = i18n.language?.startsWith('zh');
    // Convert discountPercent (e.g., 60 = 60% off) to Chinese format (e.g., 4折)
    const voucherValue = Math.round((100 - discountPercent) / 10);

    return (
      <div className="flex items-center gap-1.5 bg-[#FC8800] text-[#FEF2CF] px-3 py-1.5 rounded text-sm font-medium">
        <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
          <path
            d="M13.5 4.5L6.5 11.5L2.5 7.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>{isZh ? `${voucherValue}折` : `${discountPercent}% ${t('voucher.off', 'OFF')}`}</span>
        <span className="text-white/40 mx-0.5">|</span>
        <span>{t('voucher.validForDays', 'Valid for {{days}} days', { days: validDays })}</span>
      </div>
    );
  },
);

VoucherTag.displayName = 'VoucherTag';

// Feature item component
interface FeatureItemProps {
  feature: Feature;
  isEnterprise?: boolean;
  isLast?: boolean;
  planType?: string;
  featureIndex?: number;
}

const FeatureItem = memo(
  ({ feature, isEnterprise, isLast, planType, featureIndex }: FeatureItemProps) => {
    const parts = feature.name.split('\n');
    const name = parts[0];
    const description = parts.length > 1 ? parts.slice(1).join('\n') : null;

    // For plus plan, make the 2nd and 3rd description green
    const isGreenDescription =
      planType === 'plus' &&
      featureIndex !== undefined &&
      (featureIndex === 1 || featureIndex === 2);

    return (
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 h-5 flex items-center justify-center">
          {isEnterprise && isLast ? (
            <Wait size={16} color="rgba(28, 31, 35, 0.6)" />
          ) : (
            <div className="w-1 h-1 rounded-full bg-[#000000] " />
          )}
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm leading-5 text-refly-text-0 font-normal">
            {name}
            {description && (
              <span
                className={`font-normal ${isGreenDescription ? 'text-green-600' : 'text-refly-text-0'}`}
              >
                : {description}
              </span>
            )}
          </span>
        </div>
      </div>
    );
  },
);

FeatureItem.displayName = 'FeatureItem';

interface PlanItemProps {
  planType: string;
  title: string;
  description: string;
  features: Feature[];
  handleClick?: (interval: SubscriptionInterval) => void;
  source: PriceSource;
  loadingInfo: {
    isLoading: boolean;
    plan: string;
  };
  voucher?: Voucher | null;
  voucherValidDays?: number;
}

const PlanItem = memo((props: PlanItemProps) => {
  const { t } = useTranslation('ui');
  const {
    planType,
    title,
    description,
    features,
    handleClick,
    source,
    loadingInfo,
    voucher,
    voucherValidDays,
  } = props;
  const navigate = useNavigate();

  const { isLogin, userProfile } = useUserStoreShallow((state) => ({
    isLogin: state.isLogin,
    userProfile: state.userProfile,
  }));
  const { setLoginModalOpen } = useAuthStoreShallow((state) => ({
    setLoginModalOpen: state.setLoginModalOpen,
  }));

  const currentPlan: string = userProfile?.subscription?.planType || 'free';
  const isCurrentPlan = currentPlan === planType;
  const upgradePlan =
    PlanPriorityMap[PlanPriorityMap[currentPlan as keyof typeof PlanPriorityMap] + 1] ||
    'enterprise';
  const isUpgrade = upgradePlan === planType;
  const [isHovered, setIsHovered] = useState(false);
  const isDowngrade =
    PlanPriorityMap[currentPlan as keyof typeof PlanPriorityMap] >
    PlanPriorityMap[planType as keyof typeof PlanPriorityMap];
  const isButtonDisabledForLoggedIn = (isCurrentPlan || isDowngrade) && planType !== 'enterprise';
  const isButtonDisabled = isLogin ? isButtonDisabledForLoggedIn : false;
  const isLoadingThisPlan = isLogin && loadingInfo.isLoading && loadingInfo.plan === planType;
  const shouldShowGetStarted = !isLogin;

  const handleButtonClick = useCallback(() => {
    console.log('Button clicked', { isButtonDisabled, isLogin, planType });
    if (isButtonDisabled) return;

    // Track subscription button click event
    logEvent('subscription::price_table_click', 'settings', {
      plan_type: planType,
      interval: 'monthly',
    });

    if (isLogin) {
      handleClick?.('monthly');
    } else {
      if (source === 'page') {
        navigate('/login?returnUrl=%2Fpricing');
      } else {
        setLoginModalOpen(true);
      }
    }
  }, [handleClick, isButtonDisabled, isLogin, navigate, planType, setLoginModalOpen, source]);

  const getButtonText = useCallback(() => {
    if (shouldShowGetStarted) {
      return t('subscription.plans.getStarted');
    }
    if (isLoadingThisPlan) {
      return (
        <div className="flex items-center justify-center gap-2">
          <Spin size="small" />
          <span>{t('common.loading')}</span>
        </div>
      );
    }
    if (isCurrentPlan) {
      if (planType === 'free') {
        return (
          <span className={`text-base font-medium ${isHovered ? 'text-refly-text-2' : ''}`}>
            {t('subscription.plans.free.price')}
          </span>
        );
      }
      return (
        <span className={`text-base font-medium ${isHovered ? 'text-refly-text-2' : ''}`}>
          {t('subscription.plans.currentPlan')}
        </span>
      );
    }
    if (planType === 'free') {
      return t('subscription.plans.free.buttonText');
    }
    if (planType === 'enterprise') {
      return t('subscription.plans.enterprise.buttonText');
    }
    return (
      <span className="flex items-center justify-center gap-2 font-medium">
        {t('subscription.plans.upgrade')}
      </span>
    );
  }, [isCurrentPlan, isHovered, isLoadingThisPlan, planType, shouldShowGetStarted, t]);

  // Free plan card - simplified version
  if (planType === 'free') {
    return (
      <div
        className="w-[324px] h-[663px] p-6 box-border rounded-2xl shadow-[0px_4px_24px_rgba(0,0,0,0.08)] bg-white"
        style={{
          background: isHovered ? 'rgba(244, 244, 244, 0.8)' : '#FFFFFF',
          border: '1px solid #C9CDD4',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xl font-normal text-refly-text-0">{title}</span>
        </div>
        <p className="text-sm text-gray-500 mb-4">{description}</p>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-3xl font-normal text-refly-text-0">
            {planType === 'free'
              ? '￥0'
              : (() => {
                  const p = SUBSCRIPTION_PRICES[planType as keyof typeof SUBSCRIPTION_PRICES];
                  const sym = p?.symbol ?? '￥';
                  const price = p?.monthly || 0;
                  return `${sym}${price}`;
                })()}
          </span>
          <span className="text-sm text-gray-500">
            {(() => {
              const p = SUBSCRIPTION_PRICES[planType as keyof typeof SUBSCRIPTION_PRICES];
              return p?.currency === 'USD' ? '/mo' : '/月';
            })()}
          </span>
        </div>

        {/* Button */}
        <button
          type="button"
          className={`
            w-full h-11 rounded-[4px] text-sm font-semibold transition-all duration-200 !border-[1px] !border-solid !border-gray-200
            ${
              shouldShowGetStarted
                ? 'bg-gray-900 text-white hover:bg-gray-800 cursor-pointer'
                : isButtonDisabled
                  ? 'bg-gray-100 text-refly-text-0 cursor-pointer'
                  : 'bg-gray-900 text-white hover:bg-gray-800 cursor-pointer'
            }
          `}
          onClick={handleButtonClick}
          disabled={isLogin && isButtonDisabledForLoggedIn}
        >
          {getButtonText()}
        </button>

        {/* Features */}
        <div className="pt-3 pl-3 pb-3 mt-5 border-t border-black/[0.06] flex flex-col gap-4 bg-[#F8F8F8]">
          {features.map((feature, index) => (
            <FeatureItem key={index} feature={feature} planType={planType} featureIndex={index} />
          ))}
        </div>
      </div>
    );
  }

  // Paid plan card with pricing options
  return (
    <div
      className="w-[324px] h-[663px] p-6 box-border rounded-[8px] relative overflow-hidden"
      style={{
        background:
          isCurrentPlan || isUpgrade
            ? 'linear-gradient( 180deg, rgba(176,185,255,0.1) 0%, #FFFFFF 100%)'
            : isHovered
              ? 'linear-gradient( 180deg, rgba(176,185,255,0.1) 0%, #FFFFFF 100%)'
              : '#ffffff',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Gradient border using ::before pseudo-element */}
      <div
        className="absolute inset-0 rounded-[8px] pointer-events-none z-10"
        style={{
          padding: '1px',
          background:
            'linear-gradient(90deg, rgba(113, 88, 246, 1), rgba(27, 99, 245, 1), rgba(14, 211, 249, 1))',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <img src={plusIcon} alt="Plus" className="w-[24px] h-[24px] text-gray-900" />
          <span className="text-xl font-normal text-refly-text-0">{title}</span>
          {isCurrentPlan && (
            <Tag className="!m-0 !px-2 !py-0.5 !text-sm !font-light !rounded !bg-gray-100 !text-refly-text-2 !border-gray-200">
              {t('subscription.plans.currentPlan')}
            </Tag>
          )}
        </div>
        {/* Voucher disabled - no expiration date display */}
        {false && voucher && !isCurrentPlan && voucherValidDays && (
          <VoucherTag discountPercent={voucher.discountPercent} validDays={voucherValidDays} />
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">{description}</p>

      {/* Price display - monthly only */}
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-2xl font-normal text-refly-text-0">
          {(() => {
            const p = SUBSCRIPTION_PRICES[planType as keyof typeof SUBSCRIPTION_PRICES];
            const sym = p?.symbol ?? '￥';
            const price = p?.monthly || 0;
            return `${sym}${price}`;
          })()}
        </span>
        <span className="text-xs text-gray-500">
          {(() => {
            const p = SUBSCRIPTION_PRICES[planType as keyof typeof SUBSCRIPTION_PRICES];
            return p?.currency === 'USD' ? '/mo' : '/月';
          })()}
        </span>
      </div>

      {/* Button */}
      <Tooltip
        title={
          isButtonDisabled && !isCurrentPlan
            ? "Legacy plans can't be switched to Plus directly.\n\nPlease contact support@refly.ai"
            : undefined
        }
        placement="top"
      >
        <button
          type="button"
          className={`
            w-full h-11 rounded-[4px] text-sm font-semibold transition-all duration-200 hover:cursor-pointer
            flex items-center justify-center gap-2
            ${
              shouldShowGetStarted
                ? 'bg-gray-900 text-white hover:bg-gray-800'
                : isButtonDisabled
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : isLoadingThisPlan
                    ? 'bg-gray-800 text-white cursor-not-allowed opacity-80'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
            }
          `}
          onClick={handleButtonClick}
          disabled={isLogin && (isButtonDisabledForLoggedIn || isLoadingThisPlan)}
        >
          {getButtonText()}
        </button>
      </Tooltip>

      {/* Features */}
      <div className="pt-3 pl-3 pb-3 mt-5 border-t border-black/[0.06] flex flex-col gap-4 bg-[#F8F8F8]">
        {features.map((feature, index) => (
          <FeatureItem
            key={index}
            feature={feature}
            isEnterprise={planType === 'enterprise'}
            isLast={index === features.length - 1}
            planType={planType}
            featureIndex={index}
          />
        ))}
      </div>
    </div>
  );
});

PlanItem.displayName = 'PlanItem';

export const PriceContent = memo((props: { source: PriceSource; entryPoint?: string }) => {
  const { t } = useTranslation('ui');
  const navigate = useNavigate();
  const { source } = props;
  const {
    setSubscribeModalVisible: setVisible,
    availableVoucher,
    setAvailableVoucher,
    setVoucherLoading,
  } = useSubscriptionStoreShallow((state) => ({
    setSubscribeModalVisible: state.setSubscribeModalVisible,
    availableVoucher: state.availableVoucher,
    setAvailableVoucher: state.setAvailableVoucher,
    setVoucherLoading: state.setVoucherLoading,
  }));
  const { setLoginModalOpen } = useAuthStoreShallow((state) => ({
    setLoginModalOpen: state.setLoginModalOpen,
  }));
  const { isLogin } = useUserStoreShallow((state) => ({
    isLogin: state.isLogin,
  }));
  const { userProfile } = useUserStoreShallow((state) => ({
    userProfile: state.userProfile,
  }));

  const currentPlan: string = userProfile?.subscription?.planType || 'free';

  // 支付方式：'stripe' | 'jeepay'
  const [paymentProvider, setPaymentProvider] = useState<string>('jeepay');

  useEffect(() => {
    const fetchPaymentConfig = async () => {
      try {
        const response = await client.get<any>({ url: '/subscription/paymentConfig' });
        const provider = response.data?.data?.provider;
        if (provider) {
          setPaymentProvider(provider);
        }
      } catch (error) {
        console.warn('Failed to fetch payment config, using default jeepay:', error);
      }
    };
    fetchPaymentConfig();
  }, []);

  // 从 API 获取套餐列表
  const [dbPlans, setDbPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      try {
        const response = await getClient().getSubscriptionPlans();
        const plans = response.data?.data;
        if (plans && Array.isArray(plans) && plans.length > 0) {
          // 只保留 enabled 的套餐
          const enabledPlans = plans.filter((p: SubscriptionPlan) => p.enabled !== false);
          setDbPlans(enabledPlans);
        } else {
          setDbPlans([]);
          // 数据库无套餐数据时弹框提醒
          Modal.warning({
            title: t('subscription.noPlansTitle', '套餐未配置'),
            content: t(
              'subscription.noPlansContent',
              '订阅套餐数据尚未配置，请联系管理员在 subscription_plans 表中添加套餐数据后再试。',
            ),
          });
        }
      } catch (error) {
        console.error('Failed to fetch subscription plans:', error);
        setDbPlans([]);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, [t]);

  // 从数据库套餐中提取价格信息（以 planType 为 key，统一转换为小写）
  const dbPriceMap = useMemo(() => {
    const map: Record<string, { monthly: number; yearly: number }> = {};
    for (const plan of dbPlans) {
      if (!plan.planType) continue;
      const planTypeLower = plan.planType.toLowerCase();
      if (!map[planTypeLower]) {
        map[planTypeLower] = { monthly: 0, yearly: 0 };
      }
      if (plan.interval === 'monthly') {
        map[planTypeLower].monthly = plan.price ?? 0;
      } else if (plan.interval === 'yearly') {
        map[planTypeLower].yearly = plan.price ?? 0;
      }
    }
    return map;
  }, [dbPlans]);

  // 获取要展示的 planType 列表（从数据库获取，去重）
  const displayPlanTypes = useMemo(() => {
    if (dbPlans.length === 0) return ['free', 'plus'];
    const types = new Set<string>();
    types.add('free'); // free 始终展示
    for (const plan of dbPlans) {
      if (plan.planType && plan.planType !== 'free') {
        // 统一转换为小写，以匹配翻译文件的 key
        types.add(plan.planType.toLowerCase());
      }
    }
    return Array.from(types);
  }, [dbPlans]);

  // Calculate voucher valid days
  const voucherValidDays = useMemo(() => {
    if (!availableVoucher?.expiresAt) return 7;
    return Math.max(
      1,
      Math.ceil(
        (new Date(availableVoucher.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      ),
    );
  }, [availableVoucher]);

  // Fetch available vouchers when component mounts
  useEffect(() => {
    if (!isLogin) return;

    const fetchVouchers = async () => {
      setVoucherLoading(true);
      try {
        const response = await getClient().getAvailableVouchers();
        if (response.data?.success && response.data.data?.bestVoucher) {
          setAvailableVoucher(response.data.data.bestVoucher);
        } else {
          setAvailableVoucher(null);
        }
      } catch (error) {
        console.error('Failed to fetch available vouchers:', error);
        setAvailableVoucher(null);
      } finally {
        setVoucherLoading(false);
      }
    };

    fetchVouchers();
  }, [isLogin, setAvailableVoucher, setVoucherLoading]);

  // Report pricing view event when component mounts
  useEffect(() => {
    logEvent('pricing_view', Date.now(), {
      user_plan: currentPlan,
      has_voucher: !!availableVoucher,
      voucher_discount: availableVoucher?.discountPercent,
    });
  }, [currentPlan, availableVoucher]);

  const plansData = useMemo(() => {
    const data: Record<string, { title: string; description: string; features: Feature[] }> = {};
    for (const planType of displayPlanTypes) {
      const rawFeatures =
        (t(`subscription.plans.${planType}.features`, { returnObjects: true }) as
          | (string | Feature)[]
          | undefined) || [];
      data[planType] = {
        title: t(`subscription.plans.${planType}.title`),
        description: t(`subscription.plans.${planType}.description`),
        features: rawFeatures.map((feature) => {
          // Handle both string and object format
          if (typeof feature === 'string') {
            return { name: feature };
          }
          return feature as Feature;
        }),
      };
    }
    return data;
  }, [t, displayPlanTypes]);

  const [loadingInfo, setLoadingInfo] = useState<{
    isLoading: boolean;
    plan: string;
  }>({
    isLoading: false,
    plan: '',
  });

  const handleContactSales = useCallback(() => {
    // Redirect to enterprise version contact form
    window.location.href = 'https://tally.so/r/nWaaav';
  }, []);

  const handleFreeClick = useCallback(() => {
    if (isLogin) {
      if (source === 'modal') {
        setVisible(false);
      } else {
        navigate('/', { replace: true });
      }
    } else {
      setLoginModalOpen(true);
    }
  }, [isLogin, source, setVisible, navigate, setLoginModalOpen]);

  // Stripe 支付流程
  const handleStripeCheckout = useCallback(
    async (planType: string, interval: SubscriptionInterval) => {
      try {
        setLoadingInfo({ isLoading: true, plan: planType });

        const body: any = {
          planType: planType as SubscriptionPlanType,
          interval,
          source,
          currentPlan: currentPlan,
        };

        // 如果有优惠券，附带到请求中
        if (availableVoucher?.voucherId) {
          body.voucherId = availableVoucher.voucherId;
        }

        const res = await getClient().createCheckoutSession({ body });
        if (res.data?.data?.url) {
          window.location.href = res.data.data.url;
        } else {
          throw new Error('No checkout URL returned');
        }
      } catch (error) {
        console.error('Failed to create Stripe checkout session:', error);
        message.error(
          t('subscription.checkoutFailed', 'Failed to start checkout. Please try again.'),
        );
        setLoadingInfo({ isLoading: false, plan: '' });
      }
    },
    [source, currentPlan, availableVoucher, t],
  );

  // Jeepay (AimflyPay) 支付流程
  const handleJeepayCheckout = useCallback(
    async (planType: string, interval: SubscriptionInterval) => {
      // 优先使用 pricing.ts 常量价格（数据库可能为空）
      const staticPrice = SUBSCRIPTION_PRICES[planType as keyof typeof SUBSCRIPTION_PRICES];
      const dbPrice = dbPriceMap[planType];

      // staticPrice 为主，dbPrice 作为可选兜底
      const monthlyAmount =
        staticPrice?.monthly ?? (dbPrice?.monthly && dbPrice.monthly > 0 ? dbPrice.monthly : 0);
      const yearlyAmount =
        (staticPrice as any)?.yearlyTotal ??
        (dbPrice?.yearly && dbPrice.yearly > 0 ? dbPrice.yearly : 0);

      const amount = interval === 'yearly' ? yearlyAmount : monthlyAmount;

      if (!amount || amount <= 0) {
        message.error(t('subscription.noPriceData', '未找到该套餐的价格信息，请联系管理员。'));
        return;
      }

      const AimflyPay = (window as any).AimflyPay;
      if (!AimflyPay) {
        console.error('[AimflyPay] SDK not loaded');
        message.error(
          t('subscription.paymentSDKError', 'Payment SDK not loaded. Please refresh the page.'),
        );
        return;
      }

      try {
        setLoadingInfo({ isLoading: true, plan: planType });

        const response = await client.post<any>({
          url: '/subscription/createPayOrder',
          body: {
            planType: planType as SubscriptionPlanType,
            interval: interval,
            amount: amount,
          },
        });

        if (!response.data?.data?.payOrderId) {
          throw new Error('Failed to create payment order');
        }

        const { payOrderId } = response.data.data;

        AimflyPay.checkout({
          payOrderId: payOrderId,
          mode: 'newTab',
          notifyUrl: `${window.location.origin}/api/v1/subscription/callback`,

          onSuccess: (result: any) => {
            console.log('[AimflyPay] 支付成功', result?.payOrderId);
            message.success(t('subscription.paymentSuccess', '支付成功！'));
            if (source === 'modal') {
              setVisible(false);
            }
            setTimeout(() => {
              window.location.reload();
            }, 3000);
          },
          onCancel: () => {
            console.log('[AimflyPay] 用户取消支付');
            message.info(t('subscription.paymentCanceled', 'Payment canceled.'));
            setLoadingInfo({ isLoading: false, plan: '' });
          },
          onError: (err: any) => {
            console.error('[AimflyPay] 支付失败', err?.message);
            message.error(t('subscription.paymentFailed', 'Payment failed. Please try again.'));
            setLoadingInfo({ isLoading: false, plan: '' });
          },
        });
      } catch (error) {
        console.error('Failed to create payment order:', error);
        message.error(
          t('subscription.createOrderFailed', 'Failed to create order. Please try again.'),
        );
        setLoadingInfo({ isLoading: false, plan: '' });
      }
    },
    [dbPriceMap, t, source, setVisible],
  );

  // Airwallex 支付流程 — 使用 Airwallex SDK redirectToCheckout
  const handleAirwallexCheckout = useCallback(
    async (planType: string, interval: SubscriptionInterval) => {
      try {
        setLoadingInfo({ isLoading: true, plan: planType });

        const response = await client.post<any>({
          url: '/subscription/createAirwallexPayment',
          body: {
            planType: planType as SubscriptionPlanType,
            interval: interval,
            source,
            currentPlan: currentPlan,
          },
        });

        const data = response.data?.data;
        if (data?.error) {
          throw new Error(data.error);
        }

        if (!data?.paymentIntentId || !data?.clientSecret) {
          throw new Error('No payment intent data returned from Airwallex');
        }

        // 使用 CDN 加载的 window.AirwallexComponentsSDK
        const AirwallexSDK = (window as any).AirwallexComponentsSDK;
        if (!AirwallexSDK?.init) {
          throw new Error('Airwallex SDK not loaded. Please refresh the page.');
        }
        const awxResult = await AirwallexSDK.init({
          env: data.env || 'demo',
          enabledElements: ['hpp'],
        });
        const payments = awxResult?.payments;
        if (!payments?.redirectToCheckout) {
          throw new Error('Airwallex payments.redirectToCheckout not available');
        }
        // 动态计算 base：经具如 /AIMflyV1/workspace 减去 /workspace 得到 /AIMflyV1
        const routerBase = window.location.pathname.replace(/\/workspace.*$/, '');
        await payments.redirectToCheckout({
          mode: 'payment',
          intent_id: data.paymentIntentId,
          client_secret: data.clientSecret,
          currency: data.currency || 'USD',
          successUrl: `${window.location.origin}${routerBase}/workspace?payment=success`,
          failUrl: `${window.location.origin}${routerBase}/workspace?payment=cancel`,
        });
      } catch (error) {
        console.error('Failed to create Airwallex payment:', error);
        message.error(
          t('subscription.checkoutFailed', 'Failed to start checkout. Please try again.'),
        );
        setLoadingInfo({ isLoading: false, plan: '' });
      }
    },
    [source, currentPlan, t],
  );

  const handlePlanClick = useCallback(
    (planType: string) => (interval: SubscriptionInterval) => {
      if (planType === 'free') {
        handleFreeClick();
      } else if (planType === 'enterprise') {
        handleContactSales();
      } else {
        // 根据支付方式走不同流程
        if (paymentProvider === 'stripe') {
          handleStripeCheckout(planType, interval);
        } else if (paymentProvider === 'airwallex') {
          handleAirwallexCheckout(planType, interval);
        } else {
          handleJeepayCheckout(planType, interval);
        }
      }
    },
    [
      handleFreeClick,
      handleContactSales,
      paymentProvider,
      handleStripeCheckout,
      handleJeepayCheckout,
      handleAirwallexCheckout,
    ],
  );

  if (plansLoading) {
    return (
      <div className="subscribe-content w-full flex justify-center items-center py-20">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="subscribe-content w-full">
      <Row gutter={[24, 24]} className="subscribe-content-plans" justify="center" align="stretch">
        {displayPlanTypes
          .map((planType, index) => {
            if (planType === 'free' && currentPlan !== 'free') {
              return null;
            }
            return (
              <Col {...gridSpan} key={`${planType}-${index}`} className="flex justify-center">
                <PlanItem
                  planType={planType}
                  title={plansData[planType]?.title || planType}
                  description={plansData[planType]?.description || ''}
                  features={plansData[planType]?.features || []}
                  handleClick={handlePlanClick(planType)}
                  source={source}
                  loadingInfo={loadingInfo}
                  voucher={availableVoucher}
                  voucherValidDays={voucherValidDays}
                />
              </Col>
            );
          })
          .filter(Boolean)}
      </Row>
    </div>
  );
});

PriceContent.displayName = 'PriceContent';
